import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  styled,
  Button,
  TextField,  // Add this
  Switch,
  Select, MenuItem, Radio,
  IconButton,
  CircularProgress
} from '@mui/material';
import SurveyThemeModal from './SurveyThemeModal';
import { getApiUrl } from '../../config/api';
import API_CONFIG from '../../config/api';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import thankYouDefaultImage from '../../assets/images/thank-you.png';
import cardLayout from '../../assets/images/card-layout.svg';
import classicLayout from '../../assets/images/classic-layout.svg';
import { setClarityHost, sendClarityEvent, setClarityUser } from '../../utils/clarity';
import {
  buildThankYouSettings,
  getThankYouSettings
} from '../../utils/thankYouSettings';

// Default welcome image (we'll use the same as thank you for now, or create a new one)
const welcomeDefaultImage = thankYouDefaultImage;

const TabPanel = ({ children, value, index }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      sx={{
        p: 2,
        height: 'calc(100%)',
        overflowY: 'auto', // Add vertical scrollbar when content overflows
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },


      }}
    >
      {value === index && children}
    </Box>
  );
};

const LayoutOption = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4]
  }
}));

const SettingsModal = ({ open, onClose, onThemeSelect, onLayoutSelect, selectedTheme, surveyId,
  user, surveyData, onSurveyUpdate }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLayout, setSelectedLayout] = useState('classic'); // 'classic' or 'card'
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const [selectedTheme, setSelectedTheme] = useState(selectedTheme || null);
  const [currentTheme, setCurrentTheme] = useState(selectedTheme || null);
  const [isSaving, setIsSaving] = useState(false); // Add loading state 
  const [formTitle, setFormTitle] = useState(surveyData?.name || '');  // Add this
  const [formEnabled, setFormEnabled] = useState(true);  // Add this
  // const [formStatus, setFormStatus] = useState('enabled');
  const [showStatusModal, setShowStatusModal] = useState(false);
  // const [warningMessage, setWarningMessage] = useState('This form is currently unavailable!');
  const [tempStatus, setTempStatus] = useState('');
  const [formStatus, setFormStatus] = useState(surveyData?.form_settings?.status || 'enabled');
  const [warningMessage, setWarningMessage] = useState(surveyData?.form_settings?.warning_message || 'This form is currently unavailable!');
  const [emailRequired, setEmailRequired] = useState(surveyData?.form_settings?.email_required !== false); // Default to true for backward compatibility
  const [expirationDate, setExpirationDate] = useState(null);
  const [expirationTime, setExpirationTime] = useState(null);
  const [submissionLimit, setSubmissionLimit] = useState(surveyData?.form_settings?.submission_limit || 1000);
  const [thankYouType, setThankYouType] = useState(surveyData?.form_settings?.thank_you_type || 'page');
  const [thankYouImage, setThankYouImage] = useState(surveyData?.form_settings?.thank_you_image || thankYouDefaultImage);
  const [isEditingText, setIsEditingText] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(getThankYouSettings(surveyData?.form_settings).redirectUrl);
  const [redirectError, setRedirectError] = useState('');
  const fileInputRef = React.useRef(null);
  const [tempThankYouImage, setTempThankYouImage] = useState(null);
  const [welcomeText, setWelcomeText] = useState(surveyData?.form_settings?.welcome_text || 'Welcome to our survey!\nPlease take a moment to share your thoughts.');
  const [welcomeImage, setWelcomeImage] = useState(surveyData?.form_settings?.welcome_image || welcomeDefaultImage);
  const [isEditingWelcomeText, setIsEditingWelcomeText] = useState(false);
  const [tempWelcomeImage, setTempWelcomeImage] = useState(null);
  const welcomeFileInputRef = React.useRef(null);
  const [welcomeTitle, setWelcomeTitle] = useState(surveyData?.form_settings?.welcome_title || 'Welcome to our survey!');
  const [welcomeDescription, setWelcomeDescription] = useState(surveyData?.form_settings?.welcome_description || 'Please take a moment to share your thoughts.');
  const [thankYouTitle, setThankYouTitle] = useState(getThankYouSettings(surveyData?.form_settings).title);
  const [thankYouDescription, setThankYouDescription] = useState(getThankYouSettings(surveyData?.form_settings).message);

  useEffect(() => {
    if (!open) return;
    const thankYouSettings = getThankYouSettings(surveyData?.form_settings);
    setThankYouType(surveyData?.form_settings?.thank_you_type || 'page');
    setThankYouTitle(thankYouSettings.title);
    setThankYouDescription(thankYouSettings.message);
    setRedirectUrl(thankYouSettings.redirectUrl);
    setRedirectError('');
  }, [open, surveyData]);

  // Thank you email settings
  const [thankYouEmailEnabled, setThankYouEmailEnabled] = useState(surveyData?.form_settings?.thank_you_email?.enabled || false);
  const [thankYouEmailSubject, setThankYouEmailSubject] = useState(surveyData?.form_settings?.thank_you_email?.subject || 'Thank you for completing our survey');
  const [thankYouEmailMessage, setThankYouEmailMessage] = useState(surveyData?.form_settings?.thank_you_email?.message || 'Thank you for taking the time to complete our survey. Your feedback is valuable to us.');
  const [thankYouEmailFollowUpUrl, setThankYouEmailFollowUpUrl] = useState(surveyData?.form_settings?.thank_you_email?.follow_up_url || '');

  // Reminder email settings
  const [reminderEmailEnabled, setReminderEmailEnabled] = useState(surveyData?.form_settings?.reminder_emails?.enabled || false);
  const [reminderEmailSubject, setReminderEmailSubject] = useState(surveyData?.form_settings?.reminder_emails?.subject || 'Complete your survey - {{survey.name}}');
  const [reminderEmailMessage, setReminderEmailMessage] = useState(surveyData?.form_settings?.reminder_emails?.message || 'We noticed you started our survey but haven\'t finished it yet. Your feedback is important to us!');
  const [reminderEmailDelayHours, setReminderEmailDelayHours] = useState(surveyData?.form_settings?.reminder_emails?.delay_hours || 24);


  console.log('SettingsModal - Survey Data:', surveyData);
  
  useEffect(() => {
    if (open && user) {
      try {
        // register survey + user context with Clarity when modal opens
        setClarityUser(surveyId || null, user?.uid || user?.id || null);
      } catch (e) {
        console.warn('Failed to set clarity user from SettingsModal', e);
      }
    }
  }, [open, user, surveyId]);

const handleSaveChanges = async () => {
  console.log('Save changes clicked, activeTab:', activeTab);
  let thankYouSettings;
  try {
    thankYouSettings = buildThankYouSettings({
      title: thankYouTitle,
      message: thankYouDescription,
      redirectUrl
    });
    setRedirectError('');
  } catch (error) {
    setRedirectError(error.message);
    return;
  }
  setIsSaving(true);

  try {
    // Upload images in parallel (thank you and welcome)
    const uploadImage = async (sourceUrl, defaultUrl, tempImage, defaultName, imageType) => {
      if (sourceUrl !== defaultUrl || tempImage || surveyData?.form_settings?.[imageType]) {
        if (!tempImage) return sourceUrl;
      }

      try {
        if (!tempImage) {
          const blob = await (await fetch(defaultUrl)).blob();
          tempImage = new File([blob], defaultName, { type: blob.type });
        }

        const formData = new FormData();
        formData.append("file", tempImage);
        formData.append("survey_id", surveyId);
        formData.append("user_id", user.uid);
        formData.append("question_id", "null");
        formData.append("image_type", imageType);

        const res = await fetch(`${API_CONFIG.BASE_URL}/upload_doc.php`, {
          method: "POST",
          body: formData
        });

        const data = await res.json();
        return data.success ? data.file.url : sourceUrl;
      } catch (err) {
        console.error(`Error uploading ${imageType}`, err);
        return sourceUrl;
      }
    };

    const [finalImageUrl, finalWelcomeImageUrl] = await Promise.all([
      uploadImage(thankYouImage, thankYouDefaultImage, tempThankYouImage, "thank-you.png", "thank_you_image"),
      uploadImage(welcomeImage, welcomeDefaultImage, tempWelcomeImage, "welcome-default.png", "welcome_image")
    ]);

    // Build the full payload with ALL current state values (from all tabs)
    const payload = {
      name: formTitle,
      theme: currentTheme,
      layout: selectedLayout,
      form_settings: {
        status: formStatus,
        warning_message: warningMessage,
        email_required: emailRequired,
        thank_you_type: thankYouType,
        thank_you_image: finalImageUrl,
        welcome_title: welcomeTitle,
        welcome_description: welcomeDescription,
        welcome_image: finalWelcomeImageUrl,
        ...thankYouSettings,
        thank_you_email: {
          enabled: thankYouEmailEnabled,
          subject: thankYouEmailSubject,
          message: thankYouEmailMessage,
          follow_up_url: thankYouEmailFollowUpUrl
        },
        reminder_emails: {
          enabled: reminderEmailEnabled,
          subject: reminderEmailSubject,
          message: reminderEmailMessage,
          delay_hours: reminderEmailDelayHours
        },
        ...(formStatus === "date" && {
          expiration_date: expirationDate,
          expiration_time: expirationTime
        }),
        ...(formStatus === "limit" && { submission_limit: submissionLimit }),
        ...(formStatus === "dateAndLimit" && {
          expiration_date: expirationDate,
          expiration_time: expirationTime,
          submission_limit: submissionLimit
        })
      }
    };

    // Update the survey with the full payload
    const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.GET), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: surveyId,
        user_id: user.uid,
        ...payload
      })
    });

    const result = await res.json();
    if (result.status === "success") {
      // Update local state/callbacks as needed
      onThemeSelect?.(currentTheme);
      onLayoutSelect?.(selectedLayout);
      onSurveyUpdate?.(payload);
      setTempThankYouImage(null);
    } else {
      throw new Error(result.message || "Failed to save changes");
    }

    // Analytics
    try {
      if (user) setClarityHost(user);
      sendClarityEvent("survey_settings_saved", {
        surveyId,
        userId: user?.uid || null
      });
    } catch (e) {
      console.warn("Clarity error:", e);
    }

    handleClose();
  } catch (error) {
    console.error("Error saving changes:", error);
  } finally {
    setIsSaving(false);
  }
};



 
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus !== 'enabled') {
      setShowStatusModal(true);
      setTempStatus(newStatus);
    } else {
      setFormStatus(newStatus);
      setWarningMessage('');
    }
  };


  const handleWelcomeImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Store the file temporarily
    setTempWelcomeImage(file);

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setWelcomeImage(e.target?.result);
    };
    reader.readAsDataURL(file);
  };


  const handleThankYouImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Store the file temporarily
    setTempThankYouImage(file);

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setThankYouImage(e.target?.result);
    };
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    console.log('handleClose called');
    setActiveTab(0);
    if (onClose) {
      console.log('Calling onClose prop');
      onClose();
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLayoutSelect = (layout) => {
    console.log('SettingsModal - Selected Layout:', layout);
    setSelectedLayout(layout);
    if (onLayoutSelect) {
      onLayoutSelect(layout);
    }
  };
  const handleThemeSelect = (themeData) => {
    console.log('SettingsModal - Selected Theme:', themeData);
    // setSelectedTheme(selectedTheme);
    setCurrentTheme(themeData);
    if (onThemeSelect) {
      onThemeSelect(themeData);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={{ xs: '80%', sm: "90%" }}
      // fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: { xs: '70vh', sm: "90vh" },
          width: { xs: '80%', sm: "80%" },
          // maxWidth: '1200px'
        }
      }}
    >      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header with Close Button */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1,
          pb: 0
        }}>
          <Box sx={{ flex: 1 }} /> {/* Spacer to center tabs */}          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
              cursor: 'pointer',
              zIndex: 1000,
              backgroundColor: 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            centered
          >
            <Tab label="Themes" />

            <Tab label="Layout" />
            <Tab label="General" />
          </Tabs>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <TabPanel value={activeTab} index={0}>
            <SurveyThemeModal
              open={true}
              onClose={handleClose}
              onThemeSelect={handleThemeSelect}
              // selectedTheme={selectedTheme}
              embedded={true}
              savedTheme={selectedTheme}
            />
          </TabPanel>        <TabPanel value={activeTab} index={1}>          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: { sm: 'none', md: 'calc(95vh - 300px)' },
          }}>
            <Grid
              container
              spacing={6}
              justifyContent="center"
              alignItems="center"
              sx={{ maxWidth: '85%', mx: 'auto' }}
            >
              <Grid item xs={12} md={6}>
                <LayoutOption
                  selected={selectedLayout === 'classic'}
                  onClick={() => handleLayoutSelect('classic')}
                  sx={{
                    width: '100%',
                    mx: 'auto',
                    height: '450px'
                  }}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
                      Classic Form
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', mb: 2.5 }}>
                      All questions on one page
                    </Typography>
                    <Box
                      component="img"
                      src={classicLayout}
                      alt="Classic Layout"
                      sx={{
                        width: '100%',
                        flex: 1,
                        maxHeight: '320px',
                        objectFit: 'contain',
                        mt: 1
                      }}
                    />
                  </CardContent>
                </LayoutOption>
              </Grid>
              <Grid item xs={12} md={6}>
                <LayoutOption
                  selected={selectedLayout === 'card'}
                  onClick={() => handleLayoutSelect('card')}
                  sx={{
                    width: '100%',
                    mx: 'auto',
                    height: '450px'
                  }}                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
                      Card Form
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', mb: 2.5 }}>
                      Single question per page
                    </Typography>
                    <Box
                      component="img"
                      src={cardLayout}
                      alt="Card Layout"
                      sx={{
                        width: '100%',
                        flex: 1,
                        maxHeight: '320px',
                        objectFit: 'contain',
                        mt: 1
                      }}
                    />
                  </CardContent>
                </LayoutOption>
              </Grid>
            </Grid>
          </Box>
          </TabPanel><TabPanel value={activeTab} index={2}>
            <Box sx={{ width: '100%', mx: 'auto' }}>

              {/* Form Title */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                  Form Title
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter a name for your survey form
                </Typography>
                <TextField
                  fullWidth
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g., Customer Satisfaction Survey"
                  variant="outlined"
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white',
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />
              </Box>

              {/* Email Collection Settings */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Email Collection
                  </Typography>
                  <Switch
                    checked={emailRequired}
                    onChange={(e) => setEmailRequired(e.target.checked)}
                    color="primary"
                    size="medium"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {emailRequired
                    ? "Require email address from survey respondents"
                    : "Email collection is disabled for this survey"}
                </Typography>
                {!emailRequired && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: 'info.light',
                      color: 'info.contrastText',
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}
                  >
                    ℹ️ Anonymous responses - respondent emails will not be collected
                  </Typography>
                )}
              </Box>

              {/* Form Status */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                  Form Status
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Control when your form is available for responses
                </Typography>
                <Select
                  fullWidth
                  value={formStatus}
                  onChange={handleStatusChange}
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'white'
                    }
                  }}
                >
                  <MenuItem value="enabled">✅ Enabled - Accepting responses</MenuItem>
                  <MenuItem value="disabled">❌ Disabled - No responses accepted</MenuItem>
                  <MenuItem value="date">📅 Disable on specific date</MenuItem>
                  <MenuItem value="limit">🔢 Disable at submission limit</MenuItem>
                  <MenuItem value="dateAndLimit">⏰ Disable on date or limit</MenuItem>
                </Select>
                {formStatus !== 'enabled' && warningMessage && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'warning.light',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.main',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1
                  }}>
                    <WarningAmberIcon sx={{ color: 'warning.dark', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'warning.dark', flex: 1 }}>
                      {warningMessage}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Welcome Page Settings */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                  Welcome Page
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Customize the first impression of your survey
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Welcome Image
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-block',
                        cursor: 'pointer',
                        '&:hover .upload-overlay': {
                          opacity: 1
                        }
                      }}
                      onClick={() => welcomeFileInputRef.current?.click()}
                    >
                      <Box
                        component="img"
                        src={welcomeImage}
                        alt="Welcome"
                        onError={(e) => {
                          e.target.src = welcomeDefaultImage;
                        }}
                        sx={{
                          width: '120px',
                          height: '120px',
                          objectFit: 'cover',
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'grey.50',
                          display: 'block'
                        }}
                      />
                      <Box
                        className="upload-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s ease-in-out',
                          color: 'white'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          📸 Change Image
                        </Typography>
                      </Box>
                    </Box>
                    <input
                      type="file"
                      ref={welcomeFileInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={handleWelcomeImageUpload}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Recommended: 400x400px
                    </Typography>
                  </Box>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Welcome Title"
                        value={welcomeTitle}
                        onChange={(e) => setWelcomeTitle(e.target.value)}
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white'
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Welcome Description"
                        value={welcomeDescription}
                        onChange={(e) => setWelcomeDescription(e.target.value)}
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white'
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Thank You Page Settings */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                  Thank You Page
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Show this message after submission, then optionally redirect respondents.
                </Typography>

                {/* Action Type Selection */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant={thankYouType === 'page' ? 'contained' : 'outlined'}
                    onClick={() => setThankYouType('page')}
                    startIcon={<span>📄</span>}
                    sx={{ flex: 1, py: 1.5 }}
                  >
                    Show Thank You Page
                  </Button>
                  <Button
                    variant={thankYouType === 'redirect' ? 'contained' : 'outlined'}
                    onClick={() => setThankYouType('redirect')}
                    startIcon={<span>🔗</span>}
                    sx={{ flex: 1, py: 1.5 }}
                  >
                    Redirect after message
                  </Button>
                </Box>

                {/* Thank You Page Content */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>                  <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Thank You Image
                      </Typography>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'inline-block',
                          cursor: 'pointer',
                          '&:hover .upload-overlay': {
                            opacity: 1
                          }
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Box
                          component="img"
                          src={thankYouImage}
                          alt="Thank You"
                          onError={(e) => {
                            e.target.src = thankYouDefaultImage;
                          }}
                          sx={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'cover',
                            border: '2px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'grey.50',
                            display: 'block'
                          }}
                        />
                        <Box
                          className="upload-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out',
                            color: 'white'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            📸 Change Image
                          </Typography>
                        </Box>
                      </Box>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/*"
                        onChange={handleThankYouImageUpload}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Recommended: 400x400px
                      </Typography>
                    </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Thank You Title"
                          value={thankYouTitle}
                          onChange={(e) => setThankYouTitle(e.target.value)}
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white'
                            }
                          }}
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Thank You Message"
                          value={thankYouDescription}
                          onChange={(e) => setThankYouDescription(e.target.value)}
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white'
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                {/* Optional redirect after showing the thank-you message */}
                  <TextField
                    fullWidth
                    label="Redirect URL (Optional)"
                    placeholder="https://example.com/thank-you"
                    type="url"
                    value={redirectUrl}
                    onChange={(e) => {
                      setRedirectUrl(e.target.value);
                      setRedirectError('');
                    }}
                    error={Boolean(redirectError)}
                    helperText={redirectError || 'Respondents see the thank-you message briefly before redirecting.'}
                    size="medium"
                    sx={{
                      mt: 3,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white'
                      }
                    }}
                  />
              </Box>

              {/* Thank You Email Settings */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Thank You Email
                  </Typography>
                  <Switch
                    checked={thankYouEmailEnabled}
                    onChange={(e) => setThankYouEmailEnabled(e.target.checked)}
                    color="primary"
                    size="medium"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send a thank you email to respondents after they complete the survey
                </Typography>

                {thankYouEmailEnabled && (
                  <Box sx={{ mt: 3 }}>
                    <TextField
                      fullWidth
                      label="Email Subject"
                      value={thankYouEmailSubject}
                      onChange={(e) => setThankYouEmailSubject(e.target.value)}
                      size="medium"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Thank You Message"
                      value={thankYouEmailMessage}
                      onChange={(e) => setThankYouEmailMessage(e.target.value)}
                      size="medium"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Follow-up URL (Optional)"
                      placeholder="https://example.com/next-step"
                      value={thankYouEmailFollowUpUrl}
                      onChange={(e) => setThankYouEmailFollowUpUrl(e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                      helperText="Include a link for respondents to continue to the next step"
                    />
                  </Box>
                )}

                {!emailRequired && thankYouEmailEnabled && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      p: 1.5,
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}
                  >
                    ⚠️ Email collection is disabled. Thank you emails will not be sent.
                  </Typography>
                )}
              </Box>

              {/* Survey Reminder Email Settings */}
              <Box sx={{
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Survey Reminder Emails
                  </Typography>
                  <Switch
                    checked={reminderEmailEnabled}
                    onChange={(e) => setReminderEmailEnabled(e.target.checked)}
                    color="primary"
                    size="medium"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send reminder emails to respondents who started but haven't completed the survey
                </Typography>

                {reminderEmailEnabled && (
                  <Box sx={{ mt: 3 }}>
                    <TextField
                      fullWidth
                      label="Reminder Email Subject"
                      value={reminderEmailSubject}
                      onChange={(e) => setReminderEmailSubject(e.target.value)}
                      size="medium"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Reminder Message"
                      value={reminderEmailMessage}
                      onChange={(e) => setReminderEmailMessage(e.target.value)}
                      size="medium"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Send reminder after (hours)"
                      value={reminderEmailDelayHours}
                      onChange={(e) => setReminderEmailDelayHours(Math.max(1, parseInt(e.target.value) || 24))}
                      size="medium"
                      inputProps={{ min: 1, max: 168 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                      helperText="How many hours to wait before sending a reminder (1-168 hours)"
                    />
                  </Box>
                )}

                {!emailRequired && reminderEmailEnabled && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      p: 1.5,
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}
                  >
                    ⚠️ Email collection is disabled. Reminder emails will not be sent.
                  </Typography>
                )}
              </Box>

            </Box>
          </TabPanel>
        </Box>


        {/* Footer Buttons */}
        <Box sx={{
          p: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          background: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          mt: 'auto'
        }}>          <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'transparent'
            }
          }}
        >
            Close
          </Button>
          {/* <Button
            variant="contained"
            onClick={() => {
              if (activeTab === 0) {
                // onThemeSelect && onThemeSelect(selectedTheme);
                onThemeSelect && onThemeSelect(currentTheme); 
              } else {
                onLayoutSelect && onLayoutSelect(selectedLayout);
              }
              handleClose();
            }}
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
           Save Changes
          </Button> */}          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              '&:hover': {
                backgroundColor: isSaving ? 'primary.main' : 'primary.dark'
              },
              '&:disabled': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                opacity: 0.7
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </DialogContent>
      <Dialog
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>FORM STATUS</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the status of your form
            </Typography>


            {/* Date expiration fields */}
            {(tempStatus === 'date' || tempStatus === 'dateAndLimit') && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Expiration Date</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Disable form on a date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                    <DatePicker
                      value={expirationDate}
                      onChange={(newDate) => setExpirationDate(newDate)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth sx={{ flex: 1 }} />
                      )}
                      minDate={new Date()}
                    />
                    <Typography sx={{ mx: 1 }}>at</Typography>
                    <TimePicker
                      value={expirationTime}
                      onChange={(newTime) => setExpirationTime(newTime)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth sx={{ flex: 1 }} />
                      )}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </Typography>
                </LocalizationProvider>
              </Box>
            )}

            {/* Submission limit fields */}
            {(tempStatus === 'limit' || tempStatus === 'dateAndLimit') && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Submission Limit</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Limit the total number of submissions (e.g., 1,000)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={submissionLimit}
                  onChange={(e) => setSubmissionLimit(Math.max(1, parseInt(e.target.value) || 0))}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 1 }}
                />
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Warning Message</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set a message for disabled forms
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              value={warningMessage}
              onChange={(e) => setWarningMessage(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={() => setShowStatusModal(false)}
                sx={{ color: 'text.secondary' }}
              >
                CANCEL
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setFormStatus(tempStatus);
                  if (!warningMessage.trim()) {
                    setWarningMessage('This form is currently unavailable!');
                  }
                  // Include expiration date in form settings
                  const formSettings = {
                    status: tempStatus,
                    warning_message: warningMessage,
                    ...(tempStatus === 'date' && {
                      expiration_date: expirationDate,
                      expiration_time: expirationTime
                    }),
                    ...(tempStatus === 'limit' && {
                      submission_limit: submissionLimit
                    }),
                    ...(tempStatus === 'dateAndLimit' && {
                      expiration_date: expirationDate,
                      expiration_time: expirationTime,
                      submission_limit: submissionLimit
                    })
                  };
                  setShowStatusModal(false);
                }}
              >
                SAVE
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

    </Dialog>
  );
};

export default SettingsModal;